import { core, parseDate } from '@icc/shared/helpers';
import { startOfYesterday } from 'date-fns';
import { Operation, PartialOperation } from '../models/operation.model';
import { DocumentAttachment } from '../models/document-attachment.model';
import { SalesDocumentConnection } from '../models/sales-document-connection.model';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { Document } from '../models/document.model';
import { ExtraState, State } from '../models/state';
import { ClockService } from './clock.service';
import { Timestamp } from './timestamp';
import { IccDocument } from '../models/icc-document';
import { IccDocumentPosition } from '../models/icc-document-position';
import { SalesDocument } from '../models/sales-document.model';
import { Complaint } from '../models/complaint.model';
import { Delivery } from '../models/delivery.model';
import { ComplaintAppointment } from '../models/complaint-appointment.model';
import { ComplaintSparePart } from '../models/complaint-spare-part.model';
import { ComplaintNote } from '../models/complaint-note.model';

export const operationFunctions: Record<
    Operation['type'],
    (
        op: Operation,
        state: State,
        extraState: ExtraState
    ) => {
        state: State;
        changedDocId: string | null;
        changedField: keyof IccDocument | null;
    }
> = {
    addDoc,
    addDocMx,
    addPosition,
    addAppointment,
    addComplaintNote,
    addComplaintSparePart,
    addAttachment,
    setAttachmentField,
    addDelivery,
    setAppointmentField,
    setComplaintNoteField,
    setComplaintSparePartField,
    setField,
    setPositionField,
    setPositionPrices,
    setPositionBasePrices,
    removeAttachment,
    setDeliveryField,
    removeAppointment,
    removeComplaintSparePart,
    joinDocuments,
    detachDocuments,
    joinPositions,
    joinAttachments,
    detachPositions,
    tagDoc,
    splitedDocument,
};


export const operationIndexFunctions: {
    [key in Operation['type']]: (msg: Extract<Operation, {type: key }>) => string;
} = {
    addDoc: (msg) => msg.type + '_' + msg.docId,
    addDocMx: (msg) => msg.type + '_' + msg.docId,
    addPosition: (msg) => msg.type + '_' + msg.positionId,
    addAppointment: (msg) => msg.type + '_' + msg.appointmentId,
    addComplaintNote: (msg) => msg.type + '_' + msg.complaintNoteId,
    addComplaintSparePart: (msg) => msg.type + '_' + msg.complaintSparePartId,
    addAttachment: (msg) => msg.type + '_' + msg.attachmentId,
    setAttachmentField: (msg) => msg.type + '_' + msg.attachmentId + '_' + msg.field,
    addDelivery: (msg) => msg.type + '_' + msg.deliveryId,
    setAppointmentField: (msg) => msg.type + '_' + msg.appointmentId + '_' + msg.field,
    setComplaintNoteField: (msg) => msg.type + '_' + msg.complaintNoteId + '_' + msg.field,
    setComplaintSparePartField: (msg) => msg.type + '_' + msg.complaintSparePartId + '_' + msg.field,
    setField: (msg) => msg.type + '_' + msg.docId + '_' + msg.field,
    setPositionField: (msg) => msg.type + '_' + msg.positionId + '_' + msg.field,
    setPositionPrices: (msg) => msg.type + '_' + msg.positionId,
    setPositionBasePrices: (msg) => msg.type + '_' + msg.positionId,
    removeAttachment: (msg) => msg.type + '_' + msg.attachmentId,
    setDeliveryField: (msg) => msg.type + '_' + msg.deliveryId + '_' + msg.field,
    removeAppointment: (msg) => msg.type + '_' + msg.appointmentId,
    removeComplaintSparePart: (msg) => msg.type + '_' + msg.complaintSparePartId,
    joinAttachments: (msg) =>
        msg.type + '_' + msg.salesAttachmentId + '_' + msg.buyAttachmentId,
    joinDocuments: (msg) =>
        msg.type + '_' + msg.buyDocId + '_' + msg.docId,
    detachDocuments: (msg) =>
        msg.type + '_' + msg.buyDocId + '_' + msg.docId,
    joinPositions: (msg) =>
        msg.type +
        '_' +
        msg.buyDocId +
        '_' +
        msg.docId +
        '_' +
        msg.buyPositionId +
        '_' +
        msg.salesPositionId,
    detachPositions: (msg) =>
        msg.type +
        '_' +
        msg.buyDocId +
        '_' +
        msg.docId +
        '_' +
        msg.buyPositionId +
        '_' +
        msg.salesPositionId,
    tagDoc: (msg) => msg.type + '_' + msg.docId,
    splitedDocument: (msg) => msg.type + '_' + msg.docId,
};

export function operationIndexFunc<K extends Operation['type']>(msg: Extract<Operation, {type: K }>): string {
    return operationIndexFunctions[msg.type as K](msg);
}

// eslint-disable-next-line max-statements
function addDoc(
    op: Extract<Operation, { type: 'addDoc' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null, changedField: keyof IccDocument | null } {
    op.changes.created = Timestamp.toDate(op.timestamp);
    op.changes.modified = Timestamp.toDate(op.timestamp);
    if (op.changes.rootCreated) {
        op.changes.rootCreated = parseDate(op.changes.rootCreated);
    }
    if (op.changes.type === 'sales' && op.changes.validityDate) {
        op.changes.validityDate = parseDate(op.changes.validityDate);
    }
    if (op.changes.deletedAt) {
        op.changes.deletedAt = parseDate(op.changes.deletedAt);
    }
    if (op.changes.declaredDeadline) {
        op.changes.declaredDeadline = parseDate(op.changes.declaredDeadline);
    }
    if (op.changes.type === 'sales' && op.changes.requestedDeadline) {
        op.changes.requestedDeadline = parseDate(op.changes.requestedDeadline);
    }
    if (op.changes.type === 'complaint') {
        op.changes.modifiedForComplaintTransfer = Timestamp.toDate(op.timestamp);
    }
    const doc: IccDocument = {
        ...op.changes,
        $dirty: true,
    };
    if (state.userId && !op.changes.authorUserId) {
        op.changes.authorUserId = state.userId;
        doc.authorUserId = state.userId;
    }
    if (state.userCompanyId && !op.changes.authorCompanyId) {
        op.changes.authorCompanyId = state.userCompanyId;
        doc.authorCompanyId = state.userCompanyId;
    }
    if (!op.changes.author) {
        op.changes.author = {
            userId: op.changes.authorUserId,
            companyId: op.changes.authorCompanyId,
            referenceName: state.userReferenceName,
            email: state.userEmail,
            phoneNumber: state.userPhoneNumber,
        };
        doc.author = op.changes.author;
    }
    if (!doc.machineId) {
        op.changes.machineId = ClockService.getClock().timestamp.node();
        doc.machineId = ClockService.getClock().timestamp.node();
    }
    state.documents[doc.uuid] = doc;
    state.documentsIds.push(doc.uuid);
    state.documentTypes.set(doc.uuid, doc.type);
    extraState.positionsByDocs.set(doc.uuid, new Map());
    return { state, changedDocId: doc.uuid, changedField: null };
}

// eslint-disable-next-line max-statements
function addDocMx(
    op: Extract<Operation, { type: 'addDocMx' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    op.changes.doc.created = parseDate(op.changes.doc.created);
    op.changes.doc.modified = parseDate(op.changes.doc.modified);
    if (op.changes.doc.rootCreated) {
        op.changes.doc.rootCreated = parseDate(op.changes.doc.rootCreated);
    }
    if (op.changes.doc?.type === 'sales' && op.changes.doc.validityDate) {
        op.changes.doc.validityDate = parseDate(op.changes.doc.validityDate);
    }
    if (op.changes.doc.deletedAt) {
        op.changes.doc.deletedAt = parseDate(op.changes.doc.deletedAt);
    }
    const doc: IccDocument = {
        ...op.changes.doc,
        $dirty: true,
    };
    state.documents[doc.uuid] = doc;
    state.documentsIds.push(doc.uuid);
    state.documentTypes.set(doc.uuid, doc.type);
    extraState.positionsByDocs.set(doc.uuid, new Map());

    for (const p of op.changes.pos) {
        p.created = parseDate(p.created);
        p.modified = parseDate(p.modified);
        if (p.type !== 'defect' && p.validityDate) {
            p.validityDate = parseDate(p.validityDate);
        }
        if (p.rootCreated) {
            p.rootCreated = parseDate(p.rootCreated);
        }
        const position: IccDocumentPosition = {
            ...p,
            $dirty: true,
        };
        state.positions[position.uuid] = position;
        if (extraState.positionsByDocs.has(p.documentId)) {
            extraState.positionsByDocs.get(p.documentId).set(position.uuid, position);
        }
    }

    for (const app of op.changes.app) {
        app.created = parseDate(app.created);
        app.modified = parseDate(app.modified);
        const appointment: ComplaintAppointment = {
            ...app,
            $dirty: true,
        };
        state.appointments[appointment.uuid] = appointment;
    }

    for (const n of op.changes.note) {
        n.created = parseDate(n.created);
        n.modified = parseDate(n.modified);
        const note: ComplaintNote = {
            ...n,
            $dirty: true,
        };
        state.complaintNotes[note.uuid] = note;
    }

    for (const csp of op.changes.csp) {
        csp.created = parseDate(csp.created);
        csp.modified = parseDate(csp.modified);
        const complaintSparePart: ComplaintSparePart = {
            ...csp,
            $dirty: true,
        };
        state.complaintSpareParts[complaintSparePart.uuid] = complaintSparePart;
    }

    for (const a of op.changes.att) {
        const attachment: DocumentAttachment = {
            ...a,
            $dirty: true,
        };
        state = {
            ...state,
            attachments: [...state.attachments, attachment],
        };
    }

    for (const con  of op.changes.con) {
        const connection: SalesDocumentConnection = {
            ...con,
            $dirty: true,
        };
        state = {
            ...state,
            documentConnections: {
                ...state.documentConnections,
                [connection.salesDocumentId]: {
                    ...state.documentConnections[connection.salesDocumentId],
                    [connection.buyDocumentId]: {
                        ...state.documentConnections[connection.salesDocumentId]?.[connection.buyDocumentId],
                        [connection.uuid]: connection
                    }
                }
            }
        };
    }

    for (const p of op.changes.del) {
        p.created = parseDate(p.created);
        p.modified = parseDate(p.modified);
        const delivery: Delivery = {
            ...p,
            $dirty: true,
        };
        state.deliveries[delivery.uuid] = delivery;
    }

    return { state, changedDocId: doc.uuid, changedField: null };
}

function addPosition(
    op: Extract<Operation, { type: 'addPosition' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    op.changes.created = Timestamp.toDate(op.timestamp);
    op.changes.modified = Timestamp.toDate(op.timestamp);
    if (op.changes.type !== 'defect' && op.changes.validityDate) {
        op.changes.validityDate = parseDate(op.changes.validityDate);
    }
    if (op.changes.rootCreated) {
        op.changes.rootCreated = parseDate(op.changes.rootCreated);
    }
    const position: IccDocumentPosition = {
        ...op.changes,
        $dirty: true,
    };
    state.positions[position.uuid] = position;
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    if (extraState.positionsByDocs.has(op.changes.documentId)) {
        extraState.positionsByDocs.get(op.changes.documentId).set(position.uuid, position);
    }
    return {
        state,
        changedDocId: op.changes.documentId,
        changedField: null
    };
}

function addAppointment(
    op: Extract<Operation, { type: 'addAppointment' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    op.changes.created = Timestamp.toDate(op.timestamp);
    op.changes.modified = Timestamp.toDate(op.timestamp);
    const appointment: ComplaintAppointment = {
        ...op.changes,
        $dirty: true,
    };
    state.appointments[appointment.uuid] = appointment;
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.docId,
        changedField: null
    };
}

function removeAppointment(
    op: Extract<Operation, { type: 'removeAppointment' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    state.appointments[op.appointmentId] = {
        ...state.appointments[op.appointmentId],
        isDeleted: true,
        $dirty: true,
    };
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.docId,
        changedField: null
    };
}

function addComplaintNote(
    op: Extract<Operation, { type: 'addComplaintNote' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    op.changes.created = Timestamp.toDate(op.timestamp);
    op.changes.modified = Timestamp.toDate(op.timestamp);
    const complaintNote: ComplaintNote = {
        ...op.changes,
        $dirty: true,
    };
    state.complaintNotes[complaintNote.uuid] = complaintNote;
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.docId,
        changedField: null
    };
}

function addComplaintSparePart(
    op: Extract<Operation, { type: 'addComplaintSparePart' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    op.changes.created = Timestamp.toDate(op.timestamp);
    op.changes.modified = Timestamp.toDate(op.timestamp);
    const complaintSparePart: ComplaintSparePart = {
        ...op.changes,
        $dirty: true,
    };
    state.complaintSpareParts[complaintSparePart.uuid] = complaintSparePart;
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.docId,
        changedField: null
    };
}

function removeComplaintSparePart(
    op: Extract<Operation, { type: 'removeComplaintSparePart' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    state.complaintSpareParts[op.complaintSparePartId] = {
        ...state.complaintSpareParts[op.complaintSparePartId],
        isDeleted: true,
        $dirty: true,
    };
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.docId,
        changedField: null
    };
}

function addAttachment(
    op: Extract<Operation, { type: 'addAttachment' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null } {
    const attachment: DocumentAttachment = {
        ...op.changes,
        $dirty: true,
    };
    state = {
        ...state,
        attachments: [...state.attachments, attachment],
    };
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.changes.documentId,
        changedField: null,
    };
}

function setAttachmentField(
    op: Extract<Operation, { type: 'setAttachmentField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    const attachmentIndex = state.attachments.findIndex((doc) => doc?.uuid === op?.attachmentId);
    if (attachmentIndex) {
        if (
            op.changes &&
            (op.field === 'created' || op.field === 'modified' || op.field === 'deletedAt')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        const attachment = {
            ...state.attachments[attachmentIndex],
            [op.field]: op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.attachments[attachmentIndex] = attachment;

        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function addDelivery(
    op: Extract<Operation, { type: 'addDelivery' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null } {
    op.changes.created = Timestamp.toDate(op.timestamp);
    op.changes.modified = Timestamp.toDate(op.timestamp);
    const delivery: Delivery = {
        ...op.changes,
        $dirty: true,
    };
    state.deliveries[delivery.uuid] = delivery;
    state.documents[op.docId] = {
        ...state.documents[op.docId],
        modified: Timestamp.toDate(op.timestamp),
        $dirty: true,
    };
    return {
        state,
        changedDocId: op.changes.documentId,
        changedField: null
    };
}

function setAppointmentField(
    op: Extract<Operation, { type: 'setAppointmentField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.appointments[op.appointmentId]) {
        if (
            op.changes &&
            (op.field === 'created' || op.field === 'modified' || op.field === 'deletedAt' || op.field === 'date')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        const appointment = {
            ...state.appointments[op.appointmentId],
            [op.field]: op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.appointments[op.appointmentId] = appointment;
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function setComplaintNoteField(
    op: Extract<Operation, { type: 'setComplaintNoteField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.complaintNotes[op.complaintNoteId]) {
        if (
            op.changes &&
            (op.field === 'created' || op.field === 'modified')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        const complaintNote = {
            ...state.complaintNotes[op.complaintNoteId],
            [op.field]: op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.complaintNotes[op.complaintNoteId] = complaintNote;
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function setComplaintSparePartField(
    op: Extract<Operation, { type: 'setComplaintSparePartField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.complaintSpareParts[op.complaintSparePartId]) {
        if (
            op.changes &&
            (op.field === 'created' || op.field === 'modified' || op.field === 'deletedAt')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        const complaintSparePart = {
            ...state.complaintSpareParts[op.complaintSparePartId],
            [op.field]: op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.complaintSpareParts[op.complaintSparePartId] = complaintSparePart;
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function setField(
    op: Extract<Operation, { type: 'setField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.documents[op.docId]) {
        if (
            op.changes &&
            (op.field === 'created'||
                op.field === 'modified'||
                op.field === 'deletedAt'||
                op.field === 'rootCreated'||
                op.field === 'declaredDeadline')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        if (state.documents[op.docId].type === 'sales') {
            const field = op.field as keyof SalesDocument;
            if (op.changes && field === 'validityDate') {
                op.changes = parseDate(op.changes);
            }
            if (op.changes && field === 'requestedDeadline') {
                op.changes = parseDate(op.changes);
            }
            if (op.changes && field === 'orderPlacedAt') {
                op.changes = parseDate(op.changes);
            }
            if (op.changes && field === 'priceVersionId') {
                op.changes = Number(op.changes);
            }
        }

        if (state.documents[op.docId].type === 'complaint') {
            const field = op.field as keyof Complaint;
            if (op.changes && (field === 'purchaseDate' || field === 'modifiedForComplaintTransfer')) {
                op.changes = parseDate(op.changes);
            }
        }
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            [op.field]: op.changes,
            modified: op.field === 'isOpened' ? state.documents[op.docId].modified : Timestamp.toDate(op.timestamp),
            $dirty: true,
        };

        if (state.documents[op.docId].type === 'sales') {
            const field = op.field as keyof SalesDocument;
            if (field === 'isOrderPlaced' && op.changes === true) {
                state.documents[op.docId] = {
                    ...state.documents[op.docId],
                    orderPlacedAt: Timestamp.toDate(op.timestamp),
                } as SalesDocument;
            }
        }

        if (state.documents[op.docId].type === 'complaint') {
            const field = op.field as keyof Complaint;
            if (field !== 'distributor' &&
                field !== 'distributorCompanyId' &&
                field !== 'distributorUserId' &&
                field !== 'formerDistributors'
            ) {
                state.documents[op.docId] = {
                    ...state.documents[op.docId],
                    modifiedForComplaintTransfer: Timestamp.toDate(op.timestamp),
                } as Complaint;
            }
        }

        return {
            state,
            changedDocId: op.docId,
            changedField: op.field,
        };
    }
}

function setPositionField(
    op: Extract<Operation, { type: 'setPositionField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.positions[op.positionId]) {
        if (
            op.changes &&
            (op.field === 'created' || op.field === 'modified' || op.field === 'deletedAt' || op.field === 'rootCreated')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (state.positions[op.positionId].type !== 'defect') {
            const field = op.field as keyof SalesDocumentPosition;
            if (op.changes && field === 'validityDate') {
                op.changes = parseDate(op.changes);
            }
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        const position = {
            ...state.positions[op.positionId],
            [op.field]: op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.positions[op.positionId] = position;
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        if (extraState.positionsByDocs.has(op.docId)) {
            extraState.positionsByDocs.get(op.docId).set(op.positionId, position);
        }
        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function setPositionPrices(
    op: Extract<Operation, { type: 'setPositionPrices' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.positions[op.positionId]) {
        const position = {
            ...state.positions[op.positionId],
            ...op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.positions[op.positionId] = position;
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        extraState.positionsByDocs.get(op.docId).set(op.positionId, position);
        return {
            state,
            changedDocId: op.docId,
            changedField: null,
        };
    }
}

function setPositionBasePrices(
    op: Extract<Operation, { type: 'setPositionBasePrices' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.positions[op.positionId]) {
        const position = {
            ...state.positions[op.positionId],
            ...op.changes,
            $dirty: true,
        };
        state.positions[op.positionId] = position;
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        extraState.positionsByDocs.get(op.docId).set(op.positionId, position);
        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function removeAttachment(
    op: Extract<Operation, { type: 'removeAttachment' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    state = {
        ...state,
        attachments: state.attachments.map((doc) => {
            if (doc?.uuid === op?.attachmentId) {
                return {
                    ...doc,
                    isDeleted: true,
                    $dirty: true,
                };
            }
            return doc;
        }),
    };
    return {
        state,
        changedDocId: op.docId,
        changedField: null
    };
}

function setDeliveryField(
    op: Extract<Operation, { type: 'setDeliveryField' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (state.deliveries[op.deliveryId]) {
        if (
            op.changes &&
            (op.field === 'created' || op.field === 'modified')
        ) {
            op.changes = parseDate(op.changes);
        }
        if (op.changes === 'true') {
            op.changes = true;
        }
        if (op.changes === 'false') {
            op.changes = false;
        }
        const delivery = {
            ...state.deliveries[op.deliveryId],
            [op.field]: op.changes,
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        state.deliveries[op.deliveryId] = delivery
        state.documents[op.docId] = {
            ...state.documents[op.docId],
            modified: Timestamp.toDate(op.timestamp),
            $dirty: true,
        };
        return {
            state,
            changedDocId: op.docId,
            changedField: null
        };
    }
}

function joinDocuments(
    op: Extract<Operation, { type: 'joinDocuments' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    const connection: SalesDocumentConnection = {
        uuid: op.changes?.uuid || core.generateUUID(),
        salesDocumentId: op.docId,
        buyDocumentId: op.buyDocId,
        $dirty: true,
    };
    state = {
        ...state,
        documentConnections: {
            ...state.documentConnections,
            [op.docId]: {
                ...state.documentConnections[op.docId],
                [op.buyDocId]: {
                    ...state.documentConnections[op.docId]?.[op.buyDocId],
                    [connection.uuid]: connection
                }
            }
        }
    };

    return {
        state,
        changedDocId: null,
        changedField: null
    };
}

function joinAttachments(
    op: Extract<Operation, { type: 'joinAttachments' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    const connection: SalesDocumentConnection = {
        uuid: op.changes?.uuid || core.generateUUID(),
        salesDocumentId: op.docId,
        buyDocumentId: op.buyDocId,
        salesAttachmentId: op.salesAttachmentId,
        buyAttachmentId: op.buyAttachmentId,
        $dirty: true,
    };
    state = {
        ...state,
        documentConnections: {
            ...state.documentConnections,
            [op.docId]: {
                ...state.documentConnections[op.docId],
                [op.buyDocId]: {
                    ...state.documentConnections[op.docId]?.[op.buyDocId],
                    [connection.uuid]: connection
                }
            }
        }
    };
    return {
        state,
        changedDocId: null,
        changedField: null
    };
}

function detachDocuments(
    op: Extract<Operation, { type: 'detachDocuments' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (!state.documentConnections[op.docId]) {
        return {
            state,
            changedDocId: null,
            changedField: null
        };
    }
    const {[op.docId]: salesDocumentConnections, ...rest} = state.documentConnections;
    const {[op.buyDocId]: buyDocumentConnections, ...rest2} = salesDocumentConnections;
    state = {
        ...state,
        documentConnections: {
            ...rest,
            [op.docId]:{
                ...rest2,
                [op.buyDocId + '^']: {}
            }
        }
    };
    if (buyDocumentConnections) {
        Object.values(buyDocumentConnections).forEach(connection => {
            state.documentConnections[op.docId][op.buyDocId + '^'][connection.uuid] = {
                ...connection,
                isDeleted: true,
                $dirty: true,
            } ;
        });
    }

    return {
        state,
        changedDocId: null,
        changedField: null
    };
}

function joinPositions(
    op: Extract<Operation, { type: 'joinPositions' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    const connection: SalesDocumentConnection = {
        uuid: op.changes?.uuid || core.generateUUID(),
        salesDocumentId: op.docId,
        buyDocumentId: op.buyDocId,
        salesPositionId: op.salesPositionId,
        buyPositionId: op.buyPositionId,
        $dirty: true,
    };
    state = {
        ...state,
        documentConnections: {
            ...state.documentConnections,
            [op.docId]: {
                ...state.documentConnections[op.docId],
                [op.buyDocId]: {
                    ...state.documentConnections[op.docId]?.[op.buyDocId],
                    [connection.uuid]: connection
                }
            }
        }
    };
    return {
        state,
        changedDocId: null,
        changedField: null
    };
}

function detachPositions(
    op: Extract<Operation, { type: 'detachPositions' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null; } {
    if (!state.documents[op.docId] || !state.documentConnections[op.docId]) {
        return {
            state,
            changedDocId: null,
            changedField: null
        };
    }

    const {[op.docId]: salesDocumentConnections, ...rest} = state.documentConnections;
    const {[op.buyDocId]: buyDocumentConnections, ...rest2} = salesDocumentConnections;
    const existingDeletedBucket = rest2[op.buyDocId + '^'] || {};
    const {[op.buyDocId + '^']: _removedDeletedBucket, ...rest2WithoutDeleted} = rest2;
    state = {
        ...state,
        documentConnections: {
            ...rest,
            [op.docId]:{
                ...rest2WithoutDeleted,
                [op.buyDocId]: {

                },
                [op.buyDocId + '^']: {
                    ...existingDeletedBucket,
                }
            }
        },
    };
    if (buyDocumentConnections) {
        Object.values(buyDocumentConnections).forEach(con => {
            if (con.salesPositionId && con.buyPositionId && con.buyPositionId === op.buyPositionId && con.salesPositionId === op.salesPositionId) {
                state.documentConnections[op.docId][op.buyDocId + '^'][con.uuid] = {
                    ...con,
                    isDeleted: true,
                    $dirty: true,
                };
            } else if (con.salesPositionId && con.buyPositionId) {
                state.documentConnections[con.salesDocumentId][con.buyDocumentId][con.uuid] = con;
            }
        });

        if (Object.keys(state.documentConnections[op.docId][op.buyDocId]).length === 0) {
            delete state.documentConnections[op.docId][op.buyDocId];
        }

        if (Object.keys(state.documentConnections[op.docId][op.buyDocId + '^']).length === 0) {
            delete state.documentConnections[op.docId][op.buyDocId + '^'];
        }
    }
    return {
        state,
        changedDocId: null,
        changedField: null
    };
}

function tagDoc(
    op: Extract<Operation, { type: 'tagDoc' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null } {
    return {
        state,
        changedDocId: null,
        changedField: null
    };
}

function splitedDocument(
    op: Extract<Operation, { type: 'splitedDocument' }>,
    state: State,
    extraState: ExtraState
): { state: State; changedDocId: string | null; changedField: keyof IccDocument | null } {
    return {
        state,
        changedDocId: null,
        changedField: null
    };
}
